import { CometChatUsers } from '@cometchat/chat-uikit-react';
import type { MouseEvent } from 'react';
import { useCalls, type CallKind } from './calls/calls-context';
import { MdIcon } from './components/Icon/MdIcon';
import './ListScreen.css';

/**
 * The Users tab (plan W3.3), with a voice and a video call button on every
 * row, like Android's UsersFragment.
 *
 * The list itself is the UIKit's `CometChatUsers` (paging, search, presence);
 * the buttons go in its trailing slot. Calling goes through calls/CallProvider,
 * which runs the Calls SDK's ringing flow, so the person rings on whichever
 * Connect app they have open: web, Android or iOS.
 */
/** What a row needs from the UIKit's user object. */
interface RowUser {
  getUid(): string;
  getName(): string;
  getAvatar(): string;
}

function CallButtons({ user }: { user: RowUser }) {
  const { startCall, busy } = useCalls();
  const name = user.getName() || user.getUid();
  const call = (type: CallKind) => (e: MouseEvent) => {
    e.stopPropagation(); // the row has its own click
    startCall({ uid: user.getUid(), name, avatar: user.getAvatar() || undefined }, type);
  };
  return (
    <span className="user-call-buttons">
      <button
        type="button"
        className="md-icon-button"
        onClick={call('audio')}
        disabled={busy}
        aria-label={`Voice call ${name}`}
        title="Voice call">
        <MdIcon name="call" size={20} />
      </button>
      <button
        type="button"
        className="md-icon-button"
        onClick={call('video')}
        disabled={busy}
        aria-label={`Video call ${name}`}
        title="Video call">
        <MdIcon name="videocam" size={20} />
      </button>
    </span>
  );
}

function Users() {
  return (
    <div className="list-screen">
      <div className="list-screen__body">
        {/* The shell refetches this list each time the tab is shown again
            (AppShell's ListPanes). disableLoadingState keeps the rows on
            screen until the fresh first page replaces them; without it the
            list empties to a shimmer first. */}
        <CometChatUsers
          disableLoadingState
          trailingView={user => <CallButtons user={user} />}
        />
      </div>
    </div>
  );
}

export default Users;
